import React from 'react'
import { ChevronDown } from 'lucide-react'
import { Button, Modal } from '@/components'
import type { useUserManagementViewModel } from '../useUserManagementViewModel'

type VM = ReturnType<typeof useUserManagementViewModel>

interface EditUserModalProps {
  showEditModal: VM['showEditModal']
  onClose: () => void
  editingUser: VM['editingUser']
  showEditRoleDropdown: VM['showEditRoleDropdown']
  setShowEditRoleDropdown: VM['setShowEditRoleDropdown']
  showEditCompanyDropdown: VM['showEditCompanyDropdown']
  setShowEditCompanyDropdown: VM['setShowEditCompanyDropdown']
  editUser: VM['editUser']
  setEditUser: VM['setEditUser']
  companies: VM['companies']
  handleUpdateUser: VM['handleUpdateUser']
  /** Modal ici hata slotu: validasyon + sunucu hatasi (backend mesaji dahil). */
  error: string | null
  onDismissError: () => void
  isSubmitting: boolean
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  showEditModal,
  onClose,
  editingUser,
  showEditRoleDropdown,
  setShowEditRoleDropdown,
  showEditCompanyDropdown,
  setShowEditCompanyDropdown,
  editUser,
  setEditUser,
  companies,
  handleUpdateUser,
  error,
  onDismissError,
  isSubmitting,
}) => {
  return (
    <Modal
      isOpen={showEditModal && !!editingUser}
      onClose={onClose}
      title="Kullanıcı Düzenle"
      error={error}
      onDismissError={onDismissError}
      isSubmitting={isSubmitting}
      size="sm"
      footer={
        <div className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            İptal
          </Button>
          <Button
            onClick={handleUpdateUser}
            loading={isSubmitting}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Güncelle
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ad *
            </label>
            <input
              type="text"
              value={editUser.first_name}
              onChange={(e) => setEditUser({ ...editUser, first_name: e.target.value })}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Soyad *
            </label>
            <input
              type="text"
              value={editUser.last_name}
              onChange={(e) => setEditUser({ ...editUser, last_name: e.target.value })}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            E-posta *
          </label>
          <input
            type="email"
            value={editUser.email}
            onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
            disabled={isSubmitting}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rol *
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setShowEditRoleDropdown(!showEditRoleDropdown)
                setShowEditCompanyDropdown(false)
              }}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left flex items-center justify-between disabled:bg-gray-100"
            >
              <span>{editUser.role === 'admin' ? 'Yönetici' :
                     editUser.role === 'superadmin' ? 'Süper Yönetici' :
                     editUser.role === 'customer' ? 'Müşteri' : 'Operatör'}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showEditRoleDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showEditRoleDropdown && (
              <div className="absolute z-[999999] w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                <button
                  type="button"
                  onClick={() => {
                    setEditUser({ ...editUser, role: 'operator' })
                    setShowEditRoleDropdown(false)
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 first:rounded-t-lg"
                >
                  Operatör
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditUser({ ...editUser, role: 'admin' })
                    setShowEditRoleDropdown(false)
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50"
                >
                  Yönetici
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditUser({ ...editUser, role: 'customer' })
                    setShowEditRoleDropdown(false)
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 last:rounded-b-lg"
                >
                  Müşteri
                </button>

              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Şirket *
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setShowEditCompanyDropdown(!showEditCompanyDropdown)
                setShowEditRoleDropdown(false)
              }}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left flex items-center justify-between disabled:bg-gray-100"
            >
              <span>
                {editUser.company_id === 0
                  ? 'Şirket Seçin'
                  : companies.find(c => c.id === editUser.company_id)?.name || 'Şirket Seçin'
                }
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showEditCompanyDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showEditCompanyDropdown && (
              <div className="absolute z-[999999] w-full bottom-full mb-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                <button
                  type="button"
                  onClick={() => {
                    setEditUser({ ...editUser, company_id: 0 })
                    setShowEditCompanyDropdown(false)
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 first:rounded-t-lg text-gray-500"
                >
                  Şirket Seçin
                </button>
                {companies.map((company) => (
                  <button
                    key={company.id}
                    type="button"
                    onClick={() => {
                      setEditUser({ ...editUser, company_id: company.id })
                      setShowEditCompanyDropdown(false)
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 last:rounded-b-lg"
                  >
                    {company.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default EditUserModal
